import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { ShoppingBag, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

function NavItem({ to, children, testid }) {
    return (
        <NavLink
            to={to}
            end
            data-testid={testid}
            className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-200 ${isActive ? "text-black" : "text-zinc-500 hover:text-black"}`
            }
        >
            {children}
        </NavLink>
    );
}

export default function Shell({ children, variant = "public" }) {
    const { user, logout } = useAuth();
    const { totalQty } = useCart() || { totalQty: 0 };
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => { await logout(); navigate("/login"); };

    const links = variant === "admin" ? [
        { to: "/admin", label: "Dashboard", testid: "nav-admin-dashboard" },
        { to: "/admin/clinics", label: "Clinics", testid: "nav-admin-clinics" },
        { to: "/admin/orders", label: "Orders", testid: "nav-admin-orders" },
        { to: "/admin/products", label: "Products", testid: "nav-admin-products" },
        { to: "/admin/downloads", label: "Downloads", testid: "nav-admin-downloads" },
        { to: "/admin/promotions", label: "Promotions", testid: "nav-admin-promotions" },
        { to: "/admin/events", label: "Events", testid: "nav-admin-events" },
    ] : variant === "clinic" ? [
        { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" },
        { to: "/catalogue", label: "Catalogue", testid: "nav-catalogue" },
        { to: "/events", label: "Events", testid: "nav-events" },
        { to: "/downloads", label: "Downloads", testid: "nav-downloads" },
        { to: "/orders", label: "Orders", testid: "nav-orders" },
    ] : [
        { to: "/login", label: "Sign in", testid: "nav-login" },
        { to: "/register", label: "Register", testid: "nav-register" },
        { to: "/contact", label: "Contact", testid: "nav-contact" },
    ];

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-zinc-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
                    <div className="flex h-16 items-center justify-between">
                        <Link to={user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/"} data-testid="brand-link" className="flex items-baseline gap-3">
                            <span className="font-heading text-xl font-light tracking-[0.45em] text-black">DERMALIS</span>
                            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.3em] text-zinc-400 border-l border-zinc-200 pl-3">New Zealand · Clinic Portal</span>
                        </Link>
                        <nav className="hidden lg:flex items-center gap-8">
                            {links.map(l => <NavItem key={l.to} to={l.to} testid={l.testid}>{l.label}</NavItem>)}
                        </nav>
                        <div className="flex items-center gap-3">
                            {variant === "clinic" && (
                                <Link to="/cart" data-testid="nav-cart" className="relative inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-black px-3 py-2 transition-colors">
                                    <ShoppingBag size={18} strokeWidth={1.5} />
                                    <span className="hidden sm:inline">Cart</span>
                                    {totalQty > 0 && (
                                        <span data-testid="cart-count" className="absolute -top-0.5 -right-1 bg-black text-white text-[10px] rounded-full w-5 h-5 inline-flex items-center justify-center font-medium">{totalQty}</span>
                                    )}
                                </Link>
                            )}
                            {user && (
                                <button data-testid="logout-button" onClick={handleLogout} className="hidden sm:inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black transition-colors px-3 py-2">
                                    <LogOut size={16} strokeWidth={1.5} /> Sign out
                                </button>
                            )}
                            <button data-testid="mobile-menu-toggle" className="lg:hidden text-zinc-700" onClick={() => setOpen(o => !o)}>
                                {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
                            </button>
                        </div>
                    </div>
                    {open && (
                        <div className="lg:hidden border-t border-zinc-200 py-3 flex flex-col gap-2" data-testid="mobile-menu">
                            {links.map(l => (
                                <NavLink key={l.to} to={l.to} end onClick={() => setOpen(false)} data-testid={`mobile-${l.testid}`} className={({ isActive }) => `block px-2 py-2 text-sm ${isActive ? "text-black" : "text-zinc-600"}`}>
                                    {l.label}
                                </NavLink>
                            ))}
                            {user && (
                                <button data-testid="mobile-logout" onClick={handleLogout} className="text-left px-2 py-2 text-sm text-zinc-600">Sign out</button>
                            )}
                        </div>
                    )}
                </div>
            </header>
            <main className="flex-1">{children}</main>
            <footer className="border-t border-zinc-200 mt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="text-xs text-zinc-500 tracking-wide">© {new Date().getFullYear()} Dermalis New Zealand · Private clinic portal</div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400">For approved clinics only · portal.dermalis.co.nz</div>
                </div>
            </footer>
        </div>
    );
}
